import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Heart, Flame, ArrowRight, Cross, Music, Calendar, ShieldCheck, LogIn, Pencil } from "lucide-react";
import InlineSectionEditor from "@/components/InlineSectionEditor";
import DailyContent from "@/components/DailyContent";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/26db77d8b5ff866521aad8d3c6b356d94bc3cda254d4100b274bfa2af2574b65.png";
const TAU = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/90b9622f20e196612703a7d7c1f1ec7fa5d672234dbbf161661a04db600423c2.png";
const DOVE = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/c25b4cad97374f0313103a62c4cd65558dedf4529ed70aa000a4345f7b49b82a.png";
const FRANCIS = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/5519b50be37625ff4d4ca018949e6f7888ded3c7678c3aedde031970f954a2e3.png";
const PC_IMAGE = "https://plus.unsplash.com/premium_photo-1683147704920-4889ef7f0d53?fm=jpg&q=75&w=900&auto=format&fit=crop";

export default function Home() {
  const { user } = useAuth();
  const isLogged = !!(user && user.user_id);
  const isAdmin = user?.role === "admin";
  const [announcements, setAnnouncements] = useState([]);
  const [cms, setCms] = useState({});
  const [editing, setEditing] = useState(null); // {key, label} | null

  const reloadCms = async () => {
    try {
      const r = await api.get("/site-settings");
      setCms(r.data?.home || {});
    } catch {}
  };

  useEffect(() => {
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
    reloadCms();
  }, []);

  const heroBg = cms.banner_url || HERO_BG;
  const heroImg = cms.image_url || FRANCIS;

  const EditPencil = ({ section = "home", label = "Editar sección" }) =>
    isAdmin ? (
      <button
        type="button"
        title={label}
        aria-label={label}
        onClick={() => setEditing({ key: section, label })}
        className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold shadow hover:opacity-90 transition-opacity z-20"
        data-testid={`edit-${section}`}
      >
        <Pencil className="w-3.5 h-3.5" /> Editar
      </button>
    ) : null;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {isAdmin && <div className="absolute top-4 right-4 z-30"><EditPencil section="home" label="Editar Hero" /></div>}
        <div
          className="absolute inset-0 franciscan-pattern opacity-50"
          style={{ backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "multiply", opacity: 0.08 }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-5 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Jubileo Franciscano · 1226–2026
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary leading-[1.05] tracking-tight" data-testid="home-title">
              {cms.title ? (
                <span>{cms.title}</span>
              ) : (
                <>Catequesis<br /><span className="italic text-secondary">Virreyista</span> 2026</>
              )}
            </h1>
            <p className="font-display text-2xl md:text-3xl text-foreground/80 mt-6 italic" data-testid="home-subtitle">
              {cms.subtitle || (<>Familias Virreyistas: <span className="text-accent">¡El Señor les conceda la paz!</span></>)}
            </p>
            <p className="mt-6 text-base leading-relaxed text-foreground/70 max-w-xl" data-testid="home-paragraph">
              {cms.paragraph || "Un camino de fe, fraternidad y alegría inspirado en San Francisco de Asís. Acompañamos a niños, jóvenes y familias en el encuentro vivo con Cristo, a través de la Primera Comunión y la Confirmación."}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              {isLogged ? (
                <>
                  <Button asChild className="rounded-full px-7 h-12" data-testid="cta-primera-comunion">
                    <Link to="/primera-comunion">Primera Comunión <ArrowRight className="w-4 h-4 ml-2" /></Link>
                  </Button>
                  <Button asChild variant="secondary" className="rounded-full px-7 h-12" data-testid="cta-confirmacion">
                    <Link to="/confirmacion">Confirmación <ArrowRight className="w-4 h-4 ml-2" /></Link>
                  </Button>
                </>
              ) : (
                <Button asChild className="rounded-full px-7 h-12" data-testid="cta-login">
                  <Link to="/login"><LogIn className="w-4 h-4 mr-2" /> Acceso institucional</Link>
                </Button>
              )}
              <Button asChild variant="outline" className="rounded-full px-7 h-12" data-testid="cta-centenario">
                <Link to="/centenario">Centenario 800 años</Link>
              </Button>
              <Button asChild variant="secondary" className="rounded-full px-7 h-12" data-testid="cta-eucaristia">
                <Link to="/eucaristia"><Cross className="w-4 h-4 mr-2" /> Celebración de la Eucaristía</Link>
              </Button>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative aspect-square rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-secondary/20 to-primary/20 p-1">
              <div className="w-full h-full rounded-[2.4rem] overflow-hidden bg-card flex items-center justify-center relative">
                <img src={heroImg} alt="San Francisco" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white/90 font-display text-2xl italic leading-snug">
                    “Señor, hazme instrumento de tu paz.”
                  </p>
                  <p className="text-white/60 text-sm mt-1">— San Francisco de Asís</p>
                </div>
              </div>
            </div>
            <img src={TAU} alt="Cruz Tau" className="absolute -top-6 -right-6 w-24 h-24 animate-float opacity-90" />
            <img src={DOVE} alt="Paloma" className="absolute -bottom-8 -left-8 w-28 h-28 animate-float opacity-90" style={{ animationDelay: "1s" }} />
          </div>
        </div>
      </section>

      {/* WELCOME / BIENVENIDA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-16 relative overflow-hidden">
          {isAdmin && <div className="absolute top-4 right-4 z-30"><EditPencil section="welcome" label="Editar bienvenida" /></div>}
          <div className="absolute inset-0 opacity-10 franciscan-pattern" />
          <div className="relative max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-4">Bienvenida institucional</p>
            <h2 className="font-display text-3xl md:text-5xl leading-tight mb-6">
              Caminamos juntos como hermanos, hijos del mismo Padre.
            </h2>
            <p className="text-lg leading-relaxed opacity-90">
              Con el corazón lleno de gratitud les damos la bienvenida a este año jubilar. La Catequesis Virreyista 2026 es un regalo de Dios: un tiempo para descubrir, profundizar y celebrar la fe en familia, a la luz del Evangelio y siguiendo las huellas de San Francisco de Asís. Que esta plataforma sea instrumento de encuentro, oración y aprendizaje. <em className="text-secondary not-italic font-semibold">Pax et Bonum.</em>
            </p>
          </div>
        </div>
      </section>

      {/* DAILY CONTENT */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="flex items-center gap-3 mb-7">
          <span className="h-px flex-1 bg-border" />
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-foreground/60">Hoy en la Catequesis</p>
          <span className="h-px flex-1 bg-border" />
          {isAdmin && <EditPencil section="daily" label="Editar Hoy en la catequesis" />}
        </div>
        <DailyContent />
      </section>

      {/* TWO SECTIONS — institutional access required */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-display text-4xl md:text-5xl text-primary text-center flex-1">Dos caminos, una sola fe</h2>
          {isAdmin && <EditPencil section="dos-caminos" label="Editar Dos caminos" />}
        </div>
        {!isLogged && (
          <p className="text-center text-sm text-foreground/60 mb-10 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> Contenido formativo reservado para la familia virreyista. <Link to="/login" className="text-primary font-semibold hover:underline">Ingresa con tu correo institucional →</Link>
          </p>
        )}
        {isLogged && <div className="mb-10" />}
        <div className="grid md:grid-cols-2 gap-7">
          <Link to={isLogged ? "/primera-comunion" : "/login"} className="group rounded-3xl overflow-hidden bg-card border border-border hover:-translate-y-1 hover:shadow-xl transition-all duration-500 relative" data-testid="card-primera-comunion">
            {!isLogged && (
              <div className="absolute inset-0 bg-primary/85 z-10 flex flex-col items-center justify-center text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                <ShieldCheck className="w-10 h-10 mb-2 text-secondary" />
                <p className="font-display text-2xl">Acceso institucional</p>
                <p className="text-xs tracking-[0.2em] uppercase mt-1 text-secondary font-bold">Ingresa para ver</p>
              </div>
            )}
            <div className="aspect-[16/10] bg-gradient-to-br from-secondary/30 to-secondary/10 flex items-center justify-center overflow-hidden">
              <img src={PC_IMAGE} alt="Primera Comunión" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-7">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-secondary mb-2">Sacramento</p>
              <h3 className="font-display text-3xl text-primary group-hover:text-accent transition-colors">Primera Comunión</h3>
              <p className="mt-3 text-foreground/70">Un camino de preparación al encuentro con Jesús Eucaristía. 20 temas · 3 ciclos formativos.</p>
              <p className="mt-4 text-sm text-accent flex items-center gap-1 font-semibold">{isLogged ? "Comenzar el camino" : "Ingresar"} <ArrowRight className="w-4 h-4" /></p>
            </div>
          </Link>

          <Link to={isLogged ? "/confirmacion" : "/login"} className="group rounded-3xl overflow-hidden bg-card border border-border hover:-translate-y-1 hover:shadow-xl transition-all duration-500 relative" data-testid="card-confirmacion">
            {!isLogged && (
              <div className="absolute inset-0 bg-primary/85 z-10 flex flex-col items-center justify-center text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                <ShieldCheck className="w-10 h-10 mb-2 text-secondary" />
                <p className="font-display text-2xl">Acceso institucional</p>
                <p className="text-xs tracking-[0.2em] uppercase mt-1 text-secondary font-bold">Ingresa para ver</p>
              </div>
            )}
            <div className="aspect-[16/10] bg-gradient-to-br from-accent/30 to-accent/10 flex items-center justify-center">
              <img src={DOVE} alt="Paloma" className="w-36 h-36 group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-7">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-2">Sacramento</p>
              <h3 className="font-display text-3xl text-primary group-hover:text-accent transition-colors">Confirmación</h3>
              <p className="mt-3 text-foreground/70">Madurez en la fe y envío misionero por el Espíritu Santo. 20 temas · 3 ciclos formativos.</p>
              <p className="mt-4 text-sm text-accent flex items-center gap-1 font-semibold">{isLogged ? "Comenzar el camino" : "Ingresar"} <ArrowRight className="w-4 h-4" /></p>
            </div>
          </Link>
        </div>
      </section>

      {/* PILLARS */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        {isAdmin && <div className="flex justify-end mb-3"><EditPencil section="pilares" label="Editar pilares" /></div>}
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Heart, title: "Fraternidad", text: "Vivimos como hermanos, hijos de un mismo Padre, en la escuela de San Francisco." },
            { icon: Flame, title: "Espíritu Santo", text: "Nos deja guiar por el Espíritu que renueva la faz de la tierra y la nuestra." },
            { icon: Cross, title: "Eucaristía", text: "Centro de nuestra vida: en Cristo Pan de Vida encontramos plenitud." },
          ].map((p, i) => (
            <div key={i} className="rounded-2xl p-7 bg-card border border-border hover:-translate-y-1 hover:shadow-md transition-all duration-300">
              <span className="inline-flex w-12 h-12 rounded-full bg-secondary/15 text-secondary items-center justify-center mb-4">
                <p.icon className="w-6 h-6" />
              </span>
              <h3 className="font-display text-2xl text-primary mb-2">{p.title}</h3>
              <p className="text-foreground/70 text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ANNOUNCEMENTS */}
      {announcements.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="flex items-end justify-between mb-7">
            <h2 className="font-display text-3xl md:text-4xl text-primary">Anuncios pastorales</h2>
            <Link to="/login" className="text-sm text-accent font-semibold flex items-center gap-1">
              Ingresar <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {announcements.map((a) => (
              <div key={a.id} className="rounded-2xl p-6 bg-card border border-border" data-testid={`announcement-${a.id}`}>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-secondary mb-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {a.audience}
                </p>
                <h4 className="font-display text-xl text-primary mb-2">{a.title}</h4>
                <p className="text-sm text-foreground/70 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA CENTENARIO */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <Link to="/centenario" className="block rounded-3xl bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-10 md:p-16 hover:shadow-2xl transition-all relative overflow-hidden" data-testid="centenario-cta">
          <img src={FRANCIS} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-25" />
          <div className="relative max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3 flex items-center gap-2">
              <Music className="w-4 h-4" /> Sección especial
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mb-4">
              Octavo Centenario de la Pascua de San Francisco
            </h2>
            <p className="opacity-90 text-lg mb-6">
              1226–2026 · Recorre la vida del santo de Asís, sus frases célebres y la espiritualidad que sigue inspirando al mundo.
            </p>
            <span className="inline-flex items-center gap-2 text-secondary font-semibold">
              Entrar a la experiencia <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </section>

      {/* Inline CMS editor — admin only */}
      {isAdmin && editing && (
        <InlineSectionEditor
          open={!!editing}
          sectionKey={editing.key}
          sectionLabel={editing.label}
          onClose={() => setEditing(null)}
          onSaved={reloadCms}
        />
      )}
    </div>
  );
}
